import moment from 'moment';
import React from 'react';
import { Button, Dimmer, Divider, Form, Grid, Loader, Modal, TextArea } from 'semantic-ui-react';

import { clienteApi } from 'src/api';
import { ModalEnfocable } from 'src/componentes/semantic-ui-extended';
import { Contexto } from 'src/contexto';
import { imprimirMotivo } from 'src/modulos/Cobranzas/componentes/utilidades';
import MontoEIntereses from './MontoEIntereses';
import Pago from './Pago';
import { rutEsValido } from './rut';
import { ComandoIngresarPagoCobroCalculado, MetodosDePago, TipoDeCobroCalculado } from './tipos';

function escribirMonto(monto: number) {
    const valorConPuntos = monto.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return '$' + valorConPuntos;
}

function validarRut(posibleRut: string) {
    return posibleRut.length > 0 && rutEsValido(posibleRut);
}

const CampoCabecera: React.FunctionComponent<{
    etiqueta: string;
    valor: React.ReactNode;
}> = props => {
    const { etiqueta, valor } = props;
    return (
        <span>
            {etiqueta}:&emsp;<b>{valor}</b>
        </span>
    );
};

export interface ModalIngresarPagoCobroCalculadoProps {
    // Esta sección está orientada a los datos que se muestran
    tipoDeCobroCalculado: TipoDeCobroCalculado;
    montoInicial: number;
    deudaRestante?: number;
    modalAbierto: boolean;
    idPlanDePago: string;
    nombreCompletoApoderado: string;
    rutPasaporte: string;
    alIntentarCerrar(): void;
    alRealizarPago(): void;
}

interface State {
    metodoDePago: MetodosDePago;
    monto: number;
    interes: number;
    numeroDeBoleta: string;
    fechaRecepcionPago: string;
    numeroDeDeposito: string;
    observaciones: string;

    idInstitucionCheque: string;
    numeroDeCheque: string;
    numeroDeCuentaCheque: string;
    nombreTitularCheque: string;
    rutTitularCheque: string;
    fechaCheque: string;
    esDeTercerosCheque: boolean;
    rutEsValido: boolean;

    tipoDeCuenta: '' | 'corriente' | 'vista' | 'chequera';
    idInstitucionTransferencia: string;
    numeroDeCuentaTransferencia: string;
    nombreDelTitularTransferencia: string;
    rutTitularTransferencia: string;
    esDeTercerosTransferencia: boolean;

    tipoDeTarjeta: 'debito' | 'credito';
    idInstitucionTarjeta: string;
    tipoDeTarjetaDeCredito: 'visa' | 'mastercard' | 'americanexpress';
    numeroDeCuotasCredito: number;

    pagando: boolean;
    error: boolean;
}

class ModalIngresarPagoCobroCalculado extends React.Component<
    ModalIngresarPagoCobroCalculadoProps,
    State
> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    // tslint:disable:member-ordering
    private readonly rutOriginalEsInvalido = !validarRut(this.props.rutPasaporte);
    public state: State = {
        metodoDePago: 'efectivo',
        numeroDeDeposito: '',
        monto:
            this.props.deudaRestante !== undefined
                ? this.props.deudaRestante
                : this.props.montoInicial,
        interes: 0,
        numeroDeBoleta: '',
        fechaRecepcionPago: moment()
            .format()
            .slice(0, 10),
        observaciones: '',

        idInstitucionCheque: '',
        numeroDeCheque: '',
        numeroDeCuentaCheque: '',
        nombreTitularCheque: this.props.nombreCompletoApoderado,
        rutTitularCheque: this.rutOriginalEsInvalido ? '' : this.props.rutPasaporte,
        fechaCheque: '',
        esDeTercerosCheque: false,
        rutEsValido: false,

        tipoDeCuenta: '',
        idInstitucionTransferencia: '',
        numeroDeCuentaTransferencia: '',
        nombreDelTitularTransferencia: '',
        rutTitularTransferencia: '',
        esDeTercerosTransferencia: false,

        tipoDeTarjeta: 'debito',
        idInstitucionTarjeta: '',
        tipoDeTarjetaDeCredito: 'visa',
        numeroDeCuotasCredito: 1,

        pagando: false,
        error: false,
    };

    public render() {
        const {
            tipoDeCobroCalculado,
            montoInicial,
            deudaRestante,
            modalAbierto,
            alIntentarCerrar,
            nombreCompletoApoderado,
        } = this.props;

        const {
            metodoDePago,
            numeroDeDeposito,
            monto,
            interes,
            numeroDeBoleta,
            fechaRecepcionPago,
            observaciones,

            idInstitucionCheque,
            numeroDeCheque,
            numeroDeCuentaCheque,
            nombreTitularCheque,
            rutTitularCheque,
            fechaCheque,
            esDeTercerosCheque,

            tipoDeCuenta,
            idInstitucionTransferencia,
            numeroDeCuentaTransferencia,
            nombreDelTitularTransferencia,
            rutTitularTransferencia,
            esDeTercerosTransferencia,

            tipoDeTarjeta,
            idInstitucionTarjeta,
            tipoDeTarjetaDeCredito,
            numeroDeCuotasCredito,
        } = this.state;

        const deudaTotal = deudaRestante !== undefined ? deudaRestante : montoInicial;

        if (this.state.error) {
            return (
                <ModalEnfocable open={modalAbierto}>
                    <Modal.Header>Ocurrió un error</Modal.Header>

                    <Modal.Content>Ocurrió un error mientras se realizaba el pago.</Modal.Content>

                    <Modal.Actions>
                        <Button
                            content='Entendido'
                            onClick={() => {
                                this.setState({
                                    pagando: false,
                                    error: false,
                                });
                                alIntentarCerrar();
                            }}
                        />
                    </Modal.Actions>
                </ModalEnfocable>
            );
        }

        return (
            <ModalEnfocable size='large' open={modalAbierto}>
                <Modal.Header>Ingresar pago</Modal.Header>

                {this.state.pagando ? (
                    <Dimmer.Dimmable as={Modal.Content} dimmed>
                        <Dimmer active inverted>
                            <Loader />
                        </Dimmer>
                    </Dimmer.Dimmable>
                ) : (
                    <Modal.Content scrolling>
                        <Grid columns='4'>
                            <Grid.Column>
                                <CampoCabecera
                                    etiqueta='Tipo'
                                    valor={imprimirMotivo(tipoDeCobroCalculado)}
                                />
                            </Grid.Column>
                            <Grid.Column>
                                <CampoCabecera
                                    etiqueta='Deuda total'
                                    valor={escribirMonto(deudaTotal)}
                                />
                            </Grid.Column>
                        </Grid>

                        <Divider />

                        <Form>
                            <Grid relaxed>
                                <Grid.Column width={4}>
                                    <MontoEIntereses
                                        pagoEsperado={deudaTotal}
                                        monto={monto}
                                        interes={interes}
                                        fechaRecepcionPago={fechaRecepcionPago}
                                        numeroDeBoleta={numeroDeBoleta}
                                        alCambiarCampo={this.actualizarState}
                                    />
                                </Grid.Column>

                                <Grid.Column width={12}>
                                    <Form.Select
                                        width={6}
                                        label='Forma de pago'
                                        placeholder='Seleccione'
                                        options={[
                                            { value: 'efectivo', text: 'Efectivo' },
                                            { value: 'tarjeta', text: 'Tarjeta' },
                                            { value: 'cheque', text: 'Cheque' },
                                            { value: 'deposito', text: 'Depósito' },
                                            { value: 'transferencia', text: 'Transferencia' },
                                        ]}
                                        value={metodoDePago}
                                        onChange={(_e, { value }) => {
                                            this.actualizarState(
                                                'metodoDePago',
                                                value as MetodosDePago,
                                            );
                                        }}
                                    />
                                    <Pago
                                        metodoDePago={metodoDePago}
                                        nombreCompletoApoderado={this.props.nombreCompletoApoderado}
                                        rutPasaporteApoderado={this.props.rutPasaporte}
                                        numeroDeCheque={numeroDeCheque}
                                        idInstitucionCheque={idInstitucionCheque}
                                        numeroDeCuentaCheque={numeroDeCuentaCheque}
                                        nombreTitularCheque={
                                            esDeTercerosCheque === false
                                                ? nombreCompletoApoderado
                                                : nombreTitularCheque
                                        }
                                        rutTitularCheque={rutTitularCheque}
                                        fechaCheque={fechaCheque}
                                        esDeTercerosCheque={esDeTercerosCheque}
                                        tipoDeTarjeta={tipoDeTarjeta}
                                        tipoDeTarjetaDeCredito={tipoDeTarjetaDeCredito}
                                        numeroDeCuotasCredito={numeroDeCuotasCredito}
                                        idInstitucionTarjeta={idInstitucionTarjeta}
                                        numeroDeDeposito={numeroDeDeposito}
                                        tipoDeCuenta={tipoDeCuenta}
                                        idInstitucionTransferencia={idInstitucionTransferencia}
                                        numeroDeCuentaTransferencia={numeroDeCuentaTransferencia}
                                        nombreDelTitularTransferencia={
                                            nombreDelTitularTransferencia
                                        }
                                        rutTitularTransferencia={rutTitularTransferencia}
                                        esDeTercerosTransferencia={esDeTercerosTransferencia}
                                        alCambiarCampo={this.actualizarState}
                                    />
                                </Grid.Column>
                            </Grid>
                            <Divider />
                            <TextArea
                                placeholder='Observaciones'
                                value={observaciones}
                                onChange={(_e, { value }) => {
                                    this.actualizarState('observaciones', value as string);
                                }}
                            />
                        </Form>
                    </Modal.Content>
                )}

                <Modal.Actions>
                    <Button
                        content='Atrás'
                        disabled={this.state.pagando}
                        onClick={alIntentarCerrar}
                    />

                    <Button
                        primary
                        onClick={this.ingresarPagoCobroCalculado}
                        disabled={!this.camposSonValidos() || this.state.pagando}
                    >
                        Pagar
                    </Button>
                </Modal.Actions>
            </ModalEnfocable>
        );
    }

    private readonly actualizarState = (campo: string, valor: string | number | boolean) => {
        this.setState(prevState => {
            const nuevoState = { ...prevState, [campo]: valor };

            // Impide desmarcar `esDeTerceros` si el rut original no es válido
            nuevoState.esDeTercerosCheque =
                this.rutOriginalEsInvalido || nuevoState.esDeTercerosCheque;

            // Reinicia el rut cada vez que se cambia el checkbox `esDeTerceros`.
            // Si el rut original no es válido, no entra al bloque.
            if (campo === 'esDeTercerosCheque' && !this.rutOriginalEsInvalido) {
                nuevoState.rutTitularCheque = nuevoState.esDeTercerosCheque
                    ? ''
                    : this.props.rutPasaporte;
            }
            return nuevoState;
        });
    };

    private readonly camposSonValidos = () => {
        const { state } = this;
        if (state.monto === 0 || isNaN(state.monto)) {
            return false;
        }
        if (isNaN(state.interes)) {
            return false;
        }
        if (state.numeroDeBoleta.length === 0) {
            return false;
        }

        switch (state.metodoDePago) {
            case 'transferencia':
                return true;
            case 'cheque':
                if (state.idInstitucionCheque.length === 0) {
                    return false;
                }
                if (state.numeroDeCheque.length === 0) {
                    return false;
                }
                if (state.nombreTitularCheque.length === 0) {
                    return false;
                }
                if (state.rutTitularCheque.length === 0) {
                    return false;
                }
                if (state.fechaCheque.length === 0) {
                    return false;
                }
                if (state.esDeTercerosCheque && !state.rutEsValido) {
                    return false;
                }
                return true;
            case 'tarjeta':
                if (state.tipoDeTarjeta === 'credito' && state.numeroDeCuotasCredito < 1) {
                    return false;
                }
                return true;
            default:
                return true;
        }
    };

    private readonly ingresarPagoCobroCalculado = () => {
        // Si ya hay un pago, salir inmediatamente
        if (this.state.pagando) return;
        this.setState({ pagando: true });

        const { idPlanDePago, tipoDeCobroCalculado } = this.props;
        const {
            metodoDePago,
            monto,
            interes,
            numeroDeBoleta,
            fechaRecepcionPago,
            observaciones,

            idInstitucionCheque,
            numeroDeCheque,
            nombreTitularCheque,
            rutTitularCheque,
            fechaCheque,
            esDeTercerosCheque,

            numeroDeDeposito,

            tipoDeCuenta,
            idInstitucionTransferencia,
            numeroDeCuentaTransferencia,
            nombreDelTitularTransferencia,
            rutTitularTransferencia,
            esDeTercerosTransferencia,

            tipoDeTarjeta,
            idInstitucionTarjeta,
            tipoDeTarjetaDeCredito,
            numeroDeCuotasCredito,
        } = this.state;

        let comandoMetodoDePago: ComandoIngresarPagoCobroCalculado['metodoDePago'] | undefined;
        switch (metodoDePago) {
            case 'efectivo':
                comandoMetodoDePago = {
                    tipo: 'efectivo',
                };
                break;
            case 'deposito':
                comandoMetodoDePago = {
                    tipo: 'deposito',
                    numeroDeDeposito,
                };
                break;
            case 'transferencia':
                comandoMetodoDePago = {
                    tipo: 'transferencia',
                    tipoDeCuenta,
                    idInstitucion: idInstitucionTransferencia,
                    numeroDeCuenta: numeroDeCuentaTransferencia,
                    nombreDelTitular: nombreDelTitularTransferencia,
                    rutTitular: rutTitularTransferencia,
                    esDeTerceros: esDeTercerosTransferencia,
                };
                break;
            case 'cheque':
                comandoMetodoDePago = {
                    tipo: 'cheque',
                    idInstitucion: idInstitucionCheque,
                    numeroDeCheque,
                    nombreTitular: nombreTitularCheque,
                    rutTitular: rutTitularCheque,
                    fecha: fechaCheque,
                    esDeTerceros: esDeTercerosCheque,
                };
                break;
            case 'tarjeta':
                if (tipoDeTarjeta === 'debito') {
                    comandoMetodoDePago = {
                        tipo: 'tarjeta',
                        tipoDeTarjeta: 'debito',
                        idInstitucion: idInstitucionTarjeta,
                    };
                } else if (tipoDeTarjeta === 'credito') {
                    comandoMetodoDePago = {
                        tipo: 'tarjeta',
                        tipoDeTarjeta: 'credito',
                        idInstitucion: idInstitucionTarjeta,
                        tipoDeTarjetaDeCredito,
                        numeroDeCuotas: numeroDeCuotasCredito,
                    };
                }
                break;
            default:
                throw new Error('Quieeh?');
        }

        const comando: ComandoIngresarPagoCobroCalculado = {
            idPlanDePago,
            fecha: fechaRecepcionPago,
            monto,
            interes,
            numeroDeBoleta,
            observaciones,
            metodoDePago: comandoMetodoDePago!,
            tipoDePagoDeCobroCalculado: tipoDeCobroCalculado,
        };

        clienteApi
            .ejecutar('ingresarPagoCobroCalculado', comando)
            .then(() => this.setState({ pagando: false }, this.props.alRealizarPago))
            .catch(error => {
                this.context.mostrarModalError(error);
                this.setState({ pagando: false, error: true });
            });
    };
}

export default ModalIngresarPagoCobroCalculado;



// WEBPACK FOOTER //
// ./src/modulos/Cobranzas/componentes/FichaDeApoderado/ModalIngresarPagoCobroCalculado.tsx